import { createContext, useContext, useState } from 'react';
import {
  IPointContextType,
  IPointHistoryData,
  IPointInfo,
  IPointInvoiceData,
  TCreatePoint,
  TDropPoint,
  TFetchPointInfo,
  TFetchPointInvice,
  TFetchUserPointHistory,
  TUpdatePoint,
} from './types';
import { CLIENT_API } from '@api/services';
import { useError } from '@hook/useError';

const PointContext = createContext<IPointContextType | undefined>(undefined);

export function PointContextProvier({
  children,
}: {
  children: React.ReactNode;
}) {
  const { handleError } = useError();

  const [pointInfo, setPointInfo] = useState<IPointInfo | null>(null);
  const [pointInvoiceData, setPointInvoiceData] =
    useState<IPointInvoiceData | null>(null);
  const [pointHistoryData, setPointHistoryData] =
    useState<IPointHistoryData | null>(null);

  const fetchPointInfo: TFetchPointInfo = async (userId) => {
    try {
      const { data } =
        await CLIENT_API.PointApi.findUserIssuedPointToken(userId);
      if (data) {
        setPointInfo(data);
        return true;
      }
      return false;
    } catch (e) {
      handleError(e);
      return false;
    }
  };

  const createPoint: TCreatePoint = async (data) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.PointApi.issuePointToken(data);

      return id;
    } catch (e: any) {
      const {
        response: {
          data: { errorCode },
        },
      } = e;
      if (errorCode === 'POI-0008' || errorCode === 'POI-0009') {
        return errorCode;
      } else {
        handleError(e);
      }
    }
  };

  const updatePoint: TUpdatePoint = async ({ pointId, data }) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.PointApi.updatePointTokenSymbol(pointId, data);
      if (id) {
        if (!pointInfo) return;
        setPointInfo({ ...pointInfo, imageUrl: data.imageUrl });
      }
      return id;
    } catch (e) {
      handleError(e);
    }
  };

  const fetchPointInvice: TFetchPointInvice = async ({
    userId,
    tokenId,
    limit,
    requestPageNumber,
  }) => {
    try {
      const { data } =
        await CLIENT_API.PointApi.getOwnedTokenHistoriesByOffsetPagination(
          userId,
          tokenId,
          { limit, requestPageNumber },
        );

      if (data) {
        setPointInvoiceData(data as IPointInvoiceData);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const fetchUserPointHistory: TFetchUserPointHistory = async ({
    userId,
    tokenId,
    limit,
    cursor,
  }) => {
    try {
      const { data } = await CLIENT_API.PointApi.findUserPointTokenHistories(
        userId,
        tokenId,
        {
          limit,
          cursor,
        },
      );
      if (data) {
        setPointHistoryData({
          ...data,
          totalPage: Math.ceil(data.totalElementCount / limit),
        });
      }
    } catch (e) {
      handleError(e);
    }
  };

  const dropPoint: TDropPoint = async (params) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.PointApi.dropPointToken(params);
      if (id) {
        return id;
      }
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <PointContext.Provider
      value={{
        pointInfo,
        fetchPointInfo,
        createPoint,
        updatePoint,
        fetchPointInvice,
        pointInvoiceData,
        fetchUserPointHistory,
        pointHistoryData,
        dropPoint,
      }}
    >
      {children}
    </PointContext.Provider>
  );
}

export const usePointContext = () => {
  const context = useContext(PointContext);
  if (!context) {
    throw new Error(
      'usePointContext must be used within a PointContextProvider',
    );
  }
  return context;
};
